<template>
  <v-card flat class="weight-rep-card">
    <v-card flat class="input-card pa-4">
      <v-alert v-if="completedSets - warmupSetsCount >= (exercise.sets || []).length" type="info" dense text class="mb-4">
        Du hast alle Plan-Sätze abgeschlossen. Weitere Sätze werden mit deinen letzten Werten vorbelegt.
      </v-alert>

      <v-chip-group v-if="!isCompetition" v-model="mode" row mandatory class="timer-mode-chips mb-4">
        <v-chip value="quick" outlined> ⚡ Schnellwahl </v-chip>
        <v-chip value="manual" outlined> 🔢 Manuell </v-chip>
      </v-chip-group>

      <template v-if="mode === 'quick' || isCompetition">
        <template v-if="hasWeightSetting">
          <div class="section-title mb-2 white--text">Gewicht wählen</div>
          <v-row dense no-gutters class="mb-4">
            <v-col cols="4" v-for="(preset, idx) in weightPresets" :key="`w-${idx}`" class="pa-1">
              <v-btn
                block
                outlined
                class="preset-btn"
                :class="{
                  'preset-selected-hyrox': isHyrox && weight === preset,
                  'preset-selected': !isHyrox && weight === preset,
                  'preset-selected--warmup': weight === preset && warmupActive,
                }"
                @click="setWeight(preset)">
                {{ preset }} kg
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <div class="section-title mb-2 white--text">Wiederholungen wählen</div>
        <v-row dense no-gutters class="mb-4">
          <v-col cols="4" v-for="(preset, idx) in repPresets" :key="`r-${idx}`" class="pa-1">
            <v-btn
              block
              outlined
              class="preset-btn"
              :class="{
                'preset-selected-hyrox': isHyrox && reps === preset,
                'preset-selected': !isHyrox && reps === preset,
                'preset-selected--warmup': reps === preset && warmupActive,
              }"
              @click="setReps(preset)">
              {{ preset }}
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <template v-else-if="!isCompetition && mode === 'manual'">
        <v-row dense no-gutters class="mb-4 justify-space-between">
          <v-col v-if="hasWeightSetting" :cols="hasWeightSetting ? 6 : 12" class="pr-2">
            <v-text-field
              v-model.number="weight"
              label="Gewicht (kg)"
              type="number"
              inputmode="numeric"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-weight-lifter" />
          </v-col>
          <v-col :cols="hasWeightSetting ? 6 : 12" :class="{ 'pl-2': hasWeightSetting }">
            <v-text-field
              v-model.number="reps"
              inputmode="numeric"
              label="Wiederholungen"
              type="number"
              dense
              outlined
              hide-details
              append-inner-icon="mdi-counter" />
          </v-col>
        </v-row>
      </template>

      <div class="button-container">
        <v-btn v-if="warmupActive" text block @click="skip" class="skip-warmup-btn mb-3">
          <v-icon left>mdi-skip-next</v-icon>
          Warmup Überspringen
        </v-btn>
        <v-btn
          :color="warmupActive ? 'orange' : 'primary'"
          block
          class="add-set-btn elevation-2"
          :class="{ 'preset-selected--warmup': warmupActive, 'preset-selected': !warmupActive }"
          :disabled="!isValid"
          @click="submit">
          {{ warmupActive ? "Aufwärmsatz hinzufügen" : nextSetLabel }}
        </v-btn>
      </div>
    </v-card>
  </v-card>
</template>

<script>
export default {
  name: "WeightRepInput",
  props: ["exercise", "completedSets", "completedSetsArray", "warmup", "planSettings", "isCompetition", "isHyrox"],
  data() {
    return {
      mode: "quick",
      weight: null,
      reps: null,
    }
  },
  computed: {
    warmupActive() {
      if (!this.warmup) return false
      // Aktiv, wenn noch gar keine Sätze gemacht wurden.
      if (this.completedSets === 0) return true
      // Aktiv, wenn der einzige Satz ein übersprungener Warmup-Satz ist.
      if (this.completedSets === 1 && this.completedSetsArray[0]?.skipped) {
        return true
      }
      return false
    },
    hasWeightSetting() {
      // 1. Prüfen, ob die Vorgaben für den aktuellen Satz ein Gewicht enthalten
      if (this.planSettings && this.planSettings.some((s) => s.text === "Gewicht")) {
        return true
      }

      // 2. Fallback für zusätzliche Sätze ohne Vorgabe: Hatte der letzte Satz ein Gewicht?
      if ((!this.planSettings || this.planSettings.length === 0) && this.completedSetsArray.length > 0) {
        const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
        return lastSet.some((e) => e.text === "Gewicht")
      }

      // 3. Genereller Fallback: Erlaubt die Übungsdefinition überhaupt ein Gewicht?
      if (this.exercise && this.exercise.set_exercise_settings) {
        return this.exercise.set_exercise_settings.some((s) => s.text === "Gewicht")
      }

      return false
    },
    // NEU: Ermittelt das Zielgewicht aus den Vorgaben oder dem letzten Satz.
    targetWeight() {
      if (!this.hasWeightSetting) return 0

      // 1. Suche das geplante Gewicht in den aktuellen Satzvorgaben
      const plannedWeightSetting = this.planSettings?.find((s) => s.text === "Gewicht")
      if (plannedWeightSetting && plannedWeightSetting.value) {
        return Number(plannedWeightSetting.value)
      }

      // 2. Fallback: Wenn keine Vorgabe da ist (z.B. extra Satz), nimm das Gewicht vom letzten Satz
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => e.text === "Gewicht")?.value) || 0
    },
    targetReps() {
      const getPlannedRep = (searchText) => this.planSettings?.find((s) => s.text === searchText)?.value

      // 1. Suche die geplanten Wiederholungen in den aktuellen Satzvorgaben
      const minRep = Number(getPlannedRep("Min Wiederholungen"))
      const maxRep = Number(getPlannedRep("Max Wiederholungen"))
      const singleRep = Number(getPlannedRep("Wiederholungen")) // Für Pläne mit nur einem Wdh-Wert

      if (minRep && maxRep) {
        return Math.round((minRep + maxRep) / 2) // Nimm den Mittelwert als Vorschlag
      }
      if (minRep || maxRep) {
        return minRep || maxRep
      }
      if (singleRep) {
        return singleRep
      }

      // 2. Fallback: Nimm die Wiederholungen vom letzten gemachten Satz
      const lastSet = this.completedSetsArray.filter((s) => !s.skipped).pop() || []
      return Number(lastSet.find((e) => /Wiederholungen/.test(e.text))?.value) || 10
    },
    // In setInputTypes/weightRepInput.vue -> computed
    weightPresets() {
      if (!this.hasWeightSetting) return []
      if (this.isCompetition) {
        return [this.targetWeight]
      }
      const center = Math.max(0, this.targetWeight)

      // Dynamischer Schritt basierend auf dem Gewicht
      let step = 5
      if (center <= 10) step = 1
      else if (center <= 20) step = 2.5
      else if (center > 100) step = 10

      const presets = new Set()

      // Generiert Werte um den Mittelpunkt herum
      presets.add(center)
      presets.add(Math.max(0, center - 2 * step))
      presets.add(Math.max(0, center - step))
      presets.add(center + step)
      presets.add(center + 2 * step)
      presets.add(center + 3 * step) // Füge eine weitere Option hinzu, um die Liste aufzufüllen

      // Füge 0 nur hinzu, wenn das Zielgewicht sehr niedrig ist (z.B. bei Körpergewichtsübungen)
      if (center <= 10 && center > 0) {
        presets.add(0)
      }

      // Sortieren, Duplikate entfernen und genau 6 relevante Werte zurückgeben
      return Array.from(presets)
        .filter((p) => p >= 0)
        .sort((a, b) => a - b)
        .slice(0, 6)
    },
    repPresets() {
      if (this.isCompetition) {
        return [this.targetReps]
      }
      const center = Math.max(1, this.targetReps)
      const presets = new Set()
      presets.add(center)
      presets.add(Math.max(1, center - 2))
      presets.add(Math.max(1, center - 1))
      presets.add(center + 1)
      presets.add(center + 2)
      presets.add(center + 4)
      return Array.from(presets)
        .sort((a, b) => a - b)
        .slice(0, 6)
    },

    isValid() {
      if (this.hasWeightSetting) {
        // Wenn es eine Gewichtseinstellung gibt, muss Gewicht (kann 0 sein) und Reps > 0 gesetzt sein.
        return this.weight !== null && this.weight >= 0 && this.reps > 0
      } else {
        // Wenn es keine Gewichtseinstellung gibt, brauchen wir nur Reps > 0.
        return this.reps > 0
      }
    },

    // Bestehende Logik, die wir beibehalten:
    warmupActive() {
      return this.warmup && this.completedSets === 0
    },
    warmupSetsCount() {
      if (!this.warmup || this.completedSets === 0) return 0
      const warmupSet = this.completedSetsArray[0]
      return warmupSet && !warmupSet.skipped ? 1 : 0
    },
    realSetsCompleted() {
      return this.completedSetsArray.filter((set) => !set.skipped).length
    },
    nextSetLabel() {
      const nextNumber = this.realSetsCompleted - this.warmupSetsCount + 1
      return `${nextNumber}. Satz hinzufügen`
    },
    targetSlideIndex() {
      const totalSlides = (this.exercise?.sets?.length || 0) + (this.warmup ? 1 : 0)
      if (totalSlides === 0) return 0
      return Math.min(this.completedSets, totalSlides - 1)
    },
  },
  methods: {
    setWeight(val) {
      this.weight = val
    },
    setReps(val) {
      this.reps = val
    },

    // MODIFIZIERT: Sendet nur die relevanten Daten
    // In weightRepInput.vue -> methods
    submit() {
      if (!this.isValid) return
      const setPayload = []
      if (this.hasWeightSetting) {
        setPayload.push({ text: "Gewicht", value: this.weight })
      }
      setPayload.push({ text: "Wiederholungen", value: this.reps })

      // NEU: Markiere diesen Satz als Aufwärmsatz, wenn aktiv.
      if (this.warmupActive) {
        setPayload.isWarmup = true
      }

      this.$emit("add-set", setPayload)
      // Das Zurücksetzen der Werte nach dem Senden kann hier bleiben
      this.initializeValues()
    },

    skip() {
      this.$emit("add-set", [{ skipped: true, type: "warmup", id: Date.now() }])
    },

    // NEU: Initialisiert die Werte basierend auf den neuen computed properties
    initializeValues() {
      this.weight = this.targetWeight
      this.reps = this.targetReps
    },
  },
  watch: {
    // MODIFIZIERT: Beobachtet die planSettings und initialisiert die Werte neu.
    completedSets: {
      handler() {
        this.initializeValues()
      },
    },
    planSettings: {
      handler() {
        this.initializeValues()
      },
      immediate: true,
      deep: true,
    },
    targetSlideIndex: {
      handler(newIndex) {
        this.$emit("update-slide-index", newIndex)
      },
      immediate: true,
    },
  },
}
</script>
<style scoped>
.preset-selected-hyrox {
  border-width: 2px;
  border-color: #ffed00 !important;
  color: #ffed00 !important; /* Macht auch den Text gelb */
  box-shadow: 0 0 15px rgba(255, 237, 0, 0.7) !important;
}
.weight-rep-card {
  /* Konsistenter Glass-Effekt, wie bei den anderen Karten */

  /* Beibehaltung der Rundung und des Schattens */
  border-radius: 16px;
}
.input-card {
  background-color: rgba(44, 44, 46, 0.7);
  border-radius: 12px;
  padding-bottom: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
.timer-mode-chips .v-chip {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

.preset-btn {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.2);
  min-height: 48px;
  font-size: 1rem;
  /* NEU: Sanfter Übergang für den Glow-Effekt */
  transition: all 0.3s ease;
}
.preset-btn:hover {
  border-color: rgba(255, 255, 255, 0.5);
  background-color: rgba(255, 255, 255, 0.05);
}

.preset-selected {
  border-width: 2px;
  border-color: var(--v-primary) !important;
  /* NEU: Glow-Effekt für normale Sätze */
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.7) !important;
}
.preset-selected--warmup {
  border-width: 2px;
  border-color: orange !important;
  /* NEU: Glow-Effekt für Aufwärmsätze */
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.7) !important;
}

.button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
}
.add-set-btn {
  padding: 12px 24px;
  font-weight: 500;
  font-size: 1rem;
  border-radius: 24px;
  text-transform: none;
  width: 100%;
  max-width: 320px;
}
.skip-warmup-btn {
  color: #bdbdbd;
  width: 100%;
  max-width: 320px;
}
</style>
